import Axhelper from '@/lib/Axhelper'

/**
 * LeadsService - Handles all leads-related API operations
 * Separates API logic from React hooks and components
 */
class LeadsService {
	constructor() {
		this.baseUrl = window.RUNTIME_CONFIG.API_URL
		this.endpoints = {
			leads: `/api/leads/filters`,
			leadsDetails: `/api/leads/details`,
			leadsStats: `/api/leads/stats`,
			leadsCampaigns: `/api/leads/getcampaigns`,
			leadsDeleteOne: `/api/leads/`,
			leadsDeleteMany: `/api/leads/deletemany`,
			leadsCreate: `/api/leads/`,
			leadsUpdate: `/api/leads/`,
			leadsByCustomer: `/api/leads/bycustomer`,
			addCommunication: `/api/leads/addcommunication`,
			getCommunications: `/api/leads/getcommunications`,
		}
	}

	async getLeadById(leadId) {
		try {
			if (!leadId) {
				return {
					data: null,
					error: 'Invalid lead ID provided',
				}
			}

			const url = `${this.endpoints.leadsDetails}/${encodeURIComponent(leadId)}`
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: null,
					error: response.error,
				}
			}

			return {
				data: response.data || null,
				error: null,
			}
		} catch (error) {
			console.error('LeadsService.getLeadById error:', error)
			return {
				data: null,
				error: error.message || 'Failed to fetch lead',
			}
		}
	}

	async getLeadsWithFilters(options = {}) {
		try {
			const { limit = 50, sortColumn = 'created', sortDirection = 'desc', filters = {} } = options

			const url = this._buildFilteredUrl({
				limit,
				sortColumn,
				sortDirection,
				filters,
			})

			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return {
					data: [],
					error: response.error,
					totalCount: 0,
					hasMore: false,
				}
			}

			// Parse pagination info from headers
			const totalCount = response.headers?.['x-total-count']
				? parseInt(response.headers['x-total-count'], 10)
				: response.data?.length || 0

			const hasMore = response.data?.length < totalCount

			return {
				data: response.data || [],
				error: null,
				totalCount,
				hasMore,
				headers: response.headers,
			}
		} catch (error) {
			console.error('LeadsService.getLeadsWithFilters error:', error)
			return {
				data: [],
				error: error.message || 'Failed to fetch leads',
				totalCount: 0,
				hasMore: false,
			}
		}
	}

	/**
	 * Get lead statistics
	 * @returns {Promise<{data: Object, error: string|null}>}
	 */
	async getLeadStats() {
		try {
			const response = await Axhelper.sendGetAsync(this.endpoints.leadsStats)

			if (response.error) {
				return {
					data: {},
					error: response.error,
				}
			}

			return {
				data: response.data || {},
				error: null,
			}
		} catch (error) {
			console.error('LeadsService.getLeadStats error:', error)
			return {
				data: {},
				error: error.message || 'Failed to fetch lead statistics',
			}
		}
	}

	async getCampaigns() {
		try {
			const response = await Axhelper.sendGetAsync(this.endpoints.leadsCampaigns)
			if (response.error) {
				return {
					data: [],
					error: response.error,
				}
			}

			const campaigns = Array.isArray(response.data)
				? response.data
				: Array.isArray(response.data?.campaigns)
				? response.data.campaigns
				: []

			return {
				data: campaigns,
				error: null,
			}
		} catch (error) {
			console.error('LeadsService.getCampaigns error:', error)
			return {
				data: [],
				error: error.message || 'Failed to fetch campaign options',
			}
		}
	}

	/**
	 * Delete multiple leads
	 * @param {Array<string>} leadIds - Array of lead IDs to delete
	 * @returns {Promise<{success: boolean, error: string|null, message: string}>}
	 */
	async deleteLeads(leadIds) {
		try {
			if (!Array.isArray(leadIds) || leadIds.length === 0) {
				return { success: false, error: 'Invalid lead IDs provided', message: null }
			}

			let ok = false
			if (leadIds.length === 1) {
				const url = `${this.endpoints.leadsDeleteOne}${leadIds[0]}`
				const response = await Axhelper.sendDeleteAsync(url)
				ok = !response?.error
				if (!ok) return { success: false, error: response.error, message: null }
			} else {
				try {
					const bulk = await Axhelper.sendPostAsync(this.endpoints.leadsDeleteMany, { ids: leadIds })
					if (bulk?.error) throw new Error(bulk.error)
					ok = true
				} catch {
					const results = await Promise.allSettled(
						leadIds.map(id => Axhelper.sendDeleteAsync(`${this.endpoints.leadsDeleteOne}${id}`))
					)
					ok = results.every(r => r.status === 'fulfilled' && !r.value?.error)
					if (!ok) {
						const failed = results
							.map((r, i) => (r.status === 'fulfilled' && !r.value?.error ? null : leadIds[i]))
							.filter(Boolean)
						return { success: false, error: `Failed to delete ids: ${failed.join(', ')}`, message: null }
					}
				}
			}

			const staticMsg =
				leadIds.length > 1 ? 'Selected leads have been deleted successfully.' : 'Lead has been deleted successfully.'

			return { success: true, error: null, message: staticMsg }
		} catch (error) {
			console.error('LeadsService.deleteLeads error:', error)
			return { success: false, error: error.message || 'Failed to delete leads', message: null }
		}
	}

	async createLead(leadData) {
		try {
			const response = await Axhelper.sendPostAsync(this.endpoints.leadsCreate, leadData)
			if (response.error) {
				return {
					success: false,
					error: response.error,
				}
			}
			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('LeadsService.createLead error:', error)
			return {
				success: false,
				error: error.message || 'Failed to create lead',
			}
		}
	}

	async updateLead(leadId, leadData) {
		try {
			if (!leadId) {
				return {
					success: false,
					error: 'Invalid lead ID provided',
				}
			}

			const url = `${this.endpoints.leadsUpdate}/${encodeURIComponent(leadId)}`
			const response = await Axhelper.sendPutAsync(url, leadData)

			if (response.error) {
				return {
					success: false,
					error: response.error,
				}
			}

			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('LeadsService.updateLead error:', error)
			return {
				success: false,
				error: error.message || 'Failed to update lead',
			}
		}
	}

	async updateLeadStatus(leadId, status) {
		try {
			const response = await Axhelper.sendPutAsync(`${this.endpoints.leadsUpdate}/${encodeURIComponent(leadId)}/status`, { status })
			if (response.error) {
				return {
					success: false,
					error: response.error,
				}
			}
			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('LeadsService.updateLeadStatus error:', error)
			return {
				success: false,
				error: error.message || 'Failed to update lead status',
			}
		}
	}

	async addCommunication(payload) {
		try {
			const response = await Axhelper.sendPostAsync(this.endpoints.addCommunication, payload)
			if (response.error) {
				return {
					success: false,
					error: response.error,
				}
			}
			return {
				success: true,
				error: null,
				data: response.data || null,
			}
		} catch (error) {
			console.error('LeadsService.addCommunication error:', error)
			return {
				success: false,
				error: error.message || 'Failed to add communication',
			}
		}
	}

	/**
	 * Build URL for filtered lead requests
	 * @private
	 */
	_buildFilteredUrl({ limit, sortColumn, sortDirection, filters }) {
		let params = [`limit=${limit}`, `sortcol=${sortColumn}`, `sortdir=${sortDirection}`]

		// Add search text filter
		if (filters.searchText && filters.searchText.length >= 3) {
			params.push(`searchText=${encodeURIComponent(filters.searchText)}`)
		}

		// Add campaign filter
		if (filters.campaign) {
			params.push(`campaign=${encodeURIComponent(filters.campaign)}`)
		}

		// Add status filter
		if (filters.status) {
			params.push(`status=${encodeURIComponent(filters.status)}`)
		}

		// Add date filters
		if (filters.fromDate) {
			params.push(`fromDate=${encodeURIComponent(filters.fromDate)}`)
		}

		if (filters.toDate) {
			params.push(`toDate=${encodeURIComponent(filters.toDate)}`)
		}

		return `${this.endpoints.leads}?${params.join('&')}`
	}
}

// Export a singleton instance
export default new LeadsService()
